import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle, Filter, RefreshCw } from "lucide-react";

const SEV_COLOR = { info: "text-stone-500", warn: "text-amber-600", error: "text-red-500" };

export default function AuditLog() {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ action_prefix: "", actor_email: "", severity: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append("limit", "300");
      const [e, s] = await Promise.all([
        api.get(`/platform/audit-log?${params.toString()}`),
        api.get("/platform/audit-log/summary"),
      ]);
      setEvents(e.data.events || []);
      setSummary(s.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load audit log"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mono mb-2">Platform Audit Log</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Everything that happens across ATLAS™.</h1>
          <p className="mt-2 text-sm text-stone-600">Every login, signup, code redemption, subscription, checkout, role change, impact report, and 2FA event.</p>
        </div>
        <button onClick={load} disabled={loading} data-testid="audit-refresh" className="btn-secondary">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {summary && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="card-flat"><div className="label-mono">Total events</div><div className="kpi-num mt-2">{summary.total.toLocaleString()}</div></div>
          <div className="card-flat"><div className="label-mono">Last 24 hours</div><div className="kpi-num mt-2">{summary.last_24h.toLocaleString()}</div></div>
          <div className="card-flat md:col-span-2">
            <div className="label-mono mb-2">Top actions</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {(summary.top_actions || []).slice(0, 8).map((a) => (
                <div key={a.action} className="flex justify-between border-b border-stone-100 pb-1">
                  <code className="font-mono text-slate-950">{a.action}</code>
                  <span className="font-mono text-stone-500">{a.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="card-flat">
        <div className="flex items-center gap-2 label-mono mb-3"><Filter className="h-3 w-3" /> Filters</div>
        <div className="grid gap-3 md:grid-cols-4">
          <input value={filters.action_prefix} onChange={(e) => setFilters({ ...filters, action_prefix: e.target.value })} placeholder="Action prefix (e.g. auth. or billing.)" data-testid="audit-filter-action" className={inputCls} />
          <input value={filters.actor_email} onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })} placeholder="Actor email" className={inputCls} />
          <select value={filters.severity} onChange={(e) => setFilters({ ...filters, severity: e.target.value })} className={inputCls}>
            <option value="">All severities</option>
            <option value="info">info</option>
            <option value="warn">warn</option>
            <option value="error">error</option>
          </select>
          <button onClick={load} disabled={loading} className="btn-primary !text-xs">Apply</button>
        </div>
      </div>

      <div className="card-flat">
        <div className="label-mono mb-3">Events ({events.length})</div>
        <div className="overflow-x-auto rounded-md border border-stone-200">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50 text-left">
              <tr className="border-b border-stone-200">
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">When</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Action</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Actor</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Severity</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Metadata</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-2 font-mono text-xs text-stone-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2"><code className="font-mono text-xs text-slate-950">{e.action}</code></td>
                  <td className="px-4 py-2 text-xs">
                    {e.actor_email ? <><div className="text-slate-950">{e.actor_email}</div><div className="font-mono text-[10px] text-stone-500">{e.actor_role}</div></> : <span className="text-stone-400">—</span>}
                  </td>
                  <td className="px-4 py-2 font-mono text-[10px] uppercase"><span className={SEV_COLOR[e.severity] || "text-stone-500"}>{e.severity}</span></td>
                  <td className="px-4 py-2">
                    {e.metadata && Object.keys(e.metadata).length > 0 ? (
                      <details>
                        <summary className="cursor-pointer text-xs text-slate-950 hover:underline">view</summary>
                        <pre className="mt-1 rounded bg-stone-50 p-2 font-mono text-[10px] text-slate-900 overflow-x-auto">{JSON.stringify(e.metadata, null, 2)}</pre>
                      </details>
                    ) : <span className="text-stone-400">—</span>}
                  </td>
                </tr>
              ))}
              {events.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-xs text-stone-500">No events match filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";
